import { inverseLookup, lookup } from '@travelnest/legacy_ids';
import mem from 'mem';
import { IProperty } from '../../data/entities/property';
import { IState } from '../../store/reducer';

const memoizedGetByLegacyId = mem((legacyId: string, byId: { [key: number]: IProperty }): IProperty | undefined => {
	return Object.values(byId).find((p) => p.legacy_property_id === legacyId);
});

export const getPropertyByLegacyId = (id: string, state: IState) => memoizedGetByLegacyId(id, state.data.properties.byId);

const resolveIdType = (propertyId: string | number) => {
	const stringed = propertyId.toString();
	const inted = parseInt(stringed, 10);
	// If the input won't convert to a number, it's a legacy ID.
	// Legacy IDs beginning with a number _will_ convert to a number,
	// so we also check to see if the converted value looks different
	// to the original input - in which case, it is also legacy.
	const isLegacy = isNaN(inted) || stringed !== inted.toString();
	if (isLegacy) {
		return { type: 'legacy', id: stringed } as const;
	} else {
		return { type: 'numeric', id: inted } as const;
	}
};

/**
 * Retrieve a property from the redux state.  Properties are stored by their
 * id (an integer) and may be retrieved by this id, in number or string form, or by the
 * legacy_property_id if it exists
 *
 * @param id : The property ID or legacy ID if known
 */
export const getProperty = (id: string | number, state: IState) => {
	const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
	const isLegacy = typeof id === 'string' && (isNaN(numericId) || `${numericId}` !== id);

	if (isLegacy) {
		return getPropertyByLegacyId(<string>id, state);
	} else {
		return state.data.properties.byId[numericId];
	}
};

/**
 * Retrieve either a property's legacy_property_id if it has one,
 * or its property id if not.
 *
 * Used to allow services that don't know about new-style IDs to
 * inter-operate
 *
 * @param propertyId : The property to retrieve the ID for
 * @param state : If provided, will also check if the property exists in state
 */
export const idOrLegacyId = (propertyId: number | string): string => {
	const { id, type } = resolveIdType(propertyId);
	if (type === 'legacy') {
		return id;
	} else {
		const legacyId = lookup(id);
		return (legacyId || propertyId).toString();
	}
};

/**
 * Retrieve the property ID whether the property has a legacy ID or not.
 *
 * Used to allow services that don't know about new-style IDs to
 * inter-operate
 *
 * @param propertyId : The property to retrieve the ID for
 */
export const toNewId = (propertyId: number | string): number => {
	const { id, type } = resolveIdType(propertyId);
	if (type === 'numeric') {
		return id;
	} else {
		return inverseLookup(id)!;
	}
};
